import { useCallback, useEffect, useState } from "react";
import { BellRing, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { useNavigate } from "react-router-dom";

export const MUTATION_EVENT = "ygams:mutation";

export function NextStepPrompt() {
  const nav = useNavigate();
  const [queue, setQueue] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { try { const { data } = await api.get("/next-steps/pending"); setQueue(data); } catch {} }, []);
  useEffect(() => { load(); const id = setInterval(load, 60000); window.addEventListener(MUTATION_EVENT, load); return () => { clearInterval(id); window.removeEventListener(MUTATION_EVENT, load); }; }, [load]);
  const cur = queue[0];
  const ack = async () => { setBusy(true); try { await api.post(`/next-steps/${cur.id}/ack`, { note }); setNote(""); setQueue((q) => q.slice(1)); toast.success("Dikonfirmasi & dicatat di timeline tiket"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const later = () => setQueue((q) => q.slice(1));
  if (!cur) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && later()}>
      <DialogContent className="max-w-lg border-amber-500/40" data-testid="next-step-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600"><BellRing className="h-5 w-5" />Langkah Lanjutan — {cur.title}</DialogTitle>
          <DialogDescription className="font-mono text-xs">{cur.code} · {cur.ticket_title}{queue.length > 1 ? ` · +${queue.length - 1} pengingat lain` : ""}</DialogDescription>
        </DialogHeader>
        <div className="rounded-md border-2 border-amber-500/60 bg-amber-500/10 p-4 text-center text-base font-bold uppercase leading-relaxed tracking-wide text-amber-900 dark:text-amber-100" data-testid="next-step-text">{cur.text}</div>
        <Textarea rows={2} placeholder="Catatan (opsional), mis. sudah dilaporkan ke Bu Ani via WA" value={note} onChange={(e) => setNote(e.target.value)} data-testid="next-step-note" />
        <div className="flex flex-wrap justify-between gap-2">
          <Button variant="ghost" size="sm" onClick={() => { later(); nav(`/tickets/${cur.ticket_id}`); }} data-testid="next-step-open-ticket">Buka tiket</Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={later} data-testid="next-step-later">Ingatkan nanti</Button>
            <Button size="sm" disabled={busy} onClick={ack} data-testid="next-step-ok"><CheckCircle2 className="mr-1 h-4 w-4" />OK, sudah saya lakukan</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function TicketNextSteps({ ticketId, refetch }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => { try { const { data } = await api.get(`/next-steps/by-ticket/${ticketId}`); setRows(data); } catch {} }, [ticketId]);
  useEffect(() => { load(); window.addEventListener(MUTATION_EVENT, load); return () => window.removeEventListener(MUTATION_EVENT, load); }, [load]);
  const pending = rows.filter((r) => !r.acked_at);
  if (!pending.length) return null;
  const ack = async (id) => { try { await api.post(`/next-steps/${id}/ack`, {}); toast.success("Dikonfirmasi"); load(); refetch?.(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <section className="rounded-md border-2 border-amber-500/50 bg-amber-500/10 p-3" data-testid="ticket-next-steps">
      <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-700 dark:text-amber-300"><BellRing className="h-4 w-4" />Langkah lanjutan belum dikonfirmasi ({pending.length})</h3>
      <div className="space-y-2">{pending.map((p) => <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded border bg-background/60 p-2 text-sm" data-testid={`next-step-row-${p.id}`}><span className="font-semibold uppercase">{p.text}</span><Button size="sm" variant="outline" onClick={() => ack(p.id)} data-testid={`next-step-row-ok-${p.id}`}><CheckCircle2 className="mr-1 h-3.5 w-3.5" />OK, sudah</Button></div>)}</div>
    </section>
  );
}
